import { FC, useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';

import { actions as usersActions } from '@/api/users';
import * as customerActions from '@payaca/store/customer/customerActions';
import * as dealsActions from '@payaca/store/deals/dealsActions';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';
import * as jobPaymentsActions from '@payaca/store/jobPayments/jobPaymentsActions';
import * as jobsActions from '@payaca/store/jobs/jobsActions';

import EditJobControl from '@/ui/components/editJobControl/EditJobControl';
import PreviewAndSendJobControl from '@/ui/components/previewAndSendJobControl/PreviewAndSendJobControl';
import PreviewJobControl from '@/ui/components/previewJobControl/PreviewJobControl';
import { Button, Modal } from '@payaca/ambrosia';
import LegacyModal from '@payaca/components/modal/Modal';
import AuthenticatedPageWrapper from '../pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';

import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import { getUrlSearchParam } from '@payaca/helpers/urlHelper';

import { getCustomerByDealId, getDealByJobId } from '@/utils/stateAccessors';

import projectKeys from '@/api/queries/project/keyFactory';
import { useSelector } from '@/api/state';
import EditProposalLineItemSidebar from '@/ui/pages/newEditQuotePage/components/EditProposalLineItemSidebar';
import {
  isEstimate,
  isInvoice,
  isQuote,
} from '@payaca/helpers/jobStatusHelper';
import * as fileDownloadActions from '@payaca/store/fileDownload/fileDownloadActions';
import { FileDownloadTypes } from '@payaca/types/fileDownloadTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { downloadPdfData } from '@payaca/utilities/fileUtilities';
import { useQueryClient } from '@tanstack/react-query';
import './EditJobPage.sass';

enum ViewType {
  EDIT = 'create',
  PREVIEW = 'preview',
  SEND = 'send',
}

type Props = {
  jobId: number;
  viewType?: ViewType;
  sendJobToCustomer: (
    id: number,
    payload: {
      sendMeACopy: boolean;
      preButtonEmailText: string;
      postButtonEmailText: string;
    },
    cb: (err: any, response: any) => void
  ) => void;
  dealId?: number;
};

const EditJobPage: FC<Props> = ({
  jobId,
  viewType,
  sendJobToCustomer,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const { show } = useIntercom();
  const queryClient = useQueryClient();

  const match = useRouteMatch();
  const isEditProposalLineItemDrawOpen = useRouteMatch<{
    proposalLineItemId: string;
  }>(`${match.url}/items/:proposalLineItemId`);

  const [view, setView] = useState<ViewType>(viewType || ViewType.EDIT);
  const [validationResult, setValidationResult] = useState<{
    isValid: boolean;
    errors: string[];
  }>();
  const [errorModal, setErrorModal] = useState<{
    title: string;
    text: string;
    primaryText: string;
    primaryAction: () => void;
    secondaryText?: string;
    secondaryAction?: () => void;
  } | null>(null);

  const job = useSelector((state) => {
    return state.jobsStore.jobs && state.jobsStore.jobs[jobId]?.entity;
  });

  const deal = useSelector((state) => {
    return getDealByJobId(state, jobId);
  });

  const accountId = useSelector(
    (state: any) => state.users.myProfile.accounts[0].id
  );

  useEffect(() => {
    if (job && job.bouncedAt) {
      setView(ViewType.SEND);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'job'
  }, [job?.bouncedAt]);

  const navigateToView = useMemo(() => {
    return getUrlSearchParam(location.search, 'view');
  }, [location]);
  const dealId = useMemo(
    () => getUrlSearchParam(location.search, 'dealId'),
    [location]
  );

  useEffect(() => {
    if (
      navigateToView === ViewType.SEND &&
      validationResult &&
      validationResult.isValid
    ) {
      setView(ViewType.SEND);
    }
  }, [navigateToView, validationResult]);

  useEffect(() => {
    dispatch(usersActions.getAccountTerms(accountId));
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [accountId]);

  useEffect(() => {
    dispatch(
      jobsActions.requestGetJobValidationResult(jobId, (validationResult) => {
        setValidationResult(validationResult);
      })
    );
    dispatch(usersActions.getBusinessAttachments());

    return () => {
      dispatch(jobsActions.clearJobUpdateResults());
      dispatch(jobContentActions.clearJobLineItemUpdateResults());
      dispatch(jobContentActions.clearJobLineItemGroupUpdateResults());
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'jobId'
  }, []);

  useEffect(() => {
    if (jobId) {
      dispatch(jobsActions.requestGetJob(jobId));
      dispatch(jobsActions.requestGetJobAttachmentsForJob(jobId));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [jobId]);

  useEffect(() => {
    if (job?.jobContentId) {
      dispatch(
        jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
          job.jobContentId
        )
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [job?.jobContentId]);

  useEffect(() => {
    if (
      job &&
      !job.sentAt &&
      validationResult &&
      !validationResult.isValid &&
      view == ViewType.PREVIEW
    ) {
      setView(ViewType.EDIT);
    }
  }, [validationResult, view, job]);

  const customer = useSelector((state) => {
    if (!job) return;
    return getCustomerByDealId(state, job.dealId);
  });

  const isJobMarkedAsSentSuccessfully = useSelector((state) => {
    return state.jobsStore.isJobMarkedAsSentSuccessfully;
  });

  const markJobAsSentErrorMessage = useSelector((state) => {
    return state.jobsStore.markJobAsSentErrorMessage;
  });

  useEffect(() => {
    if (job && job?.dealId) {
      dispatch(dealsActions.requestGetDeal(job?.dealId));
      dispatch(jobPaymentsActions.requestGetJobPaymentsForDeal(job?.dealId));
      dispatch(jobsActions.requestGetJobsForDeal(job?.dealId));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'job', and 'dispatch'
  }, [job?.dealId]);

  useEffect(() => {
    if (deal?.customerId) {
      dispatch(customerActions.requestGetCustomer(deal?.customerId));
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [deal?.customerId]);

  const isJobSentSuccessfully = useMemo(() => {
    if (!job) return false;
    return !job.bouncedAt && !!job.sentAt;
  }, [job]);

  const handleErrorMessageOnSendAttempt = useCallback(
    (errorMessage: string) => {
      if (!job || !customer) return;
      if (errorMessage === 'DEMO_ENDED') {
        setErrorModal({
          title: 'Please upgrade',
          text: 'Your demo has ended, please choose a subscription to continue using Payaca',
          primaryText: 'View options',
          primaryAction: () => {
            setErrorModal(null);
            history.push('/upgradeAccount');
          },
          secondaryText: 'Contact us',
          secondaryAction: () => {
            show();
            setErrorModal(null);
          },
        });
      } else if (errorMessage === 'You have exceeded your subscription plan') {
        setErrorModal({
          title: 'Please upgrade',
          text: "You've reached the maximum number of Projects for your subscription, please upgrade your subscription to continue using Payaca",
          primaryText: 'Upgrade',
          primaryAction: () => {
            setErrorModal(null);
            history.push('/upgradeAccount');
          },
          secondaryText: 'Contact us',
          secondaryAction: () => {
            show();
            setErrorModal(null);
          },
        });
      } else if (errorMessage === 'EMAIL_INACTIVE') {
        const primaryContact = getJobContactFromCustomer(
          customer,
          job?.contactId || null
        );
        setErrorModal({
          title: 'Email address not active',
          text: `"${primaryContact?.emailAddress}" has previously rejected messages due to a bounce or spam complaint. Please edit your customer's email and try again.`,
          primaryText: 'OK',
          primaryAction: () => {
            setErrorModal(null);
          },
        });
      }
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'job', 'job.contactId', and 'show'
    [history, customer]
  );

  const navigateToDeal = useCallback(() => {
    history.replace({
      pathname: `/projects/${deal?.publicId}`,
    });
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'history'
  }, [deal]);

  const markAsSentCallback = useCallback(() => {
    if (isJobMarkedAsSentSuccessfully) {
      navigateToDeal();
    } else if (!isJobMarkedAsSentSuccessfully && markJobAsSentErrorMessage) {
      handleErrorMessageOnSendAttempt(markJobAsSentErrorMessage);
    }
  }, [
    markJobAsSentErrorMessage,
    isJobMarkedAsSentSuccessfully,
    handleErrorMessageOnSendAttempt,
    navigateToDeal,
  ]);

  const pdfFileName = useMemo(() => {
    if (!job) return '';
    if (isInvoice(job.status)) {
      return `invoice_${job.reference}.pdf`;
    } else if (isQuote(job.status)) {
      return `quote_${job.reference}.pdf`;
    } else if (isEstimate(job.status)) {
      return `estimate_${job.reference}.pdf`;
    } else {
      return `job_${job.reference}.pdf`;
    }
  }, [job]);

  const triggerFileDownload = useCallback(
    (fileContent: string) => {
      downloadPdfData(fileContent, pdfFileName);
      setIsDownloadingPdf(false);
    },
    [pdfFileName]
  );

  const handleDownloadDocument = useCallback(() => {
    if (!job) return;

    setIsDownloadingPdf(true);
    dispatch(
      fileDownloadActions.requestDownloadFile(
        jobId,
        isInvoice(job.status)
          ? FileDownloadTypes.INVOICE
          : FileDownloadTypes.QUOTE,
        triggerFileDownload
      )
    );
  }, [dispatch, jobId, job, triggerFileDownload]);

  const handleProposalLineItemPersist = () => {
    if (job) {
      dispatch(
        jobContentActions.requestGetJobContentWithJobGroupsAndJobLineItems(
          job.jobContentId
        )
      );
    }

    if (isEditProposalLineItemDrawOpen?.params.proposalLineItemId !== 'new') {
      dispatch(
        jobContentActions.requestGetJobLineItemAttachmentsForJobLineItem(
          +isEditProposalLineItemDrawOpen!.params.proposalLineItemId
        )
      );
    }

    history.push(match.url);

    if (deal?.publicId) {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.profitBreakdown(deal.publicId),
      });
    }
  };

  return (
    <>
      <AuthenticatedPageWrapper
        className={
          job && (isJobSentSuccessfully || job?.archivedAt)
            ? undefined
            : 'edit-job-page'
        }
        breadcrumbItems={[
          {
            label: 'Back',
            to: `/projects/${deal?.publicId}/${
              deal?.version === 1 ? 'proposals-and-invoices' : 'proposals'
            }`,
          },
          { label: 'Edit Proposal' },
        ]}
      >
        <div className="p-4">
          {job && (isJobSentSuccessfully || job?.archivedAt) && (
            <div>
              <PreviewJobControl
                stickyTopClassName="top-0"
                jobId={jobId}
                primaryAction={{
                  onClick: handleDownloadDocument,
                  children: (
                    <UntitledIcon name="download-01.3" className="h-5 w-5" />
                  ),
                  isProcessing: isDownloadingPdf,
                  disabled: isDownloadingPdf,
                }}
                onJobUpdateSuccess={() => {
                  dispatch(jobsActions.requestGetJob(jobId));
                }}
                onDealPaymentsUpdateSuccess={() => {
                  if (job) {
                    dispatch(dealsActions.requestGetDeal(job.dealId));

                    dispatch(
                      jobPaymentsActions.requestGetJobPaymentsForDeal(
                        job.dealId
                      )
                    );
                  }
                }}
                markAsSentCallback={markAsSentCallback}
              />
            </div>
          )}
          {job && !isJobSentSuccessfully && (
            <>
              {(view === ViewType.EDIT ||
                (view === ViewType.SEND && !job.bouncedAt)) && (
                <>
                  <EditJobControl
                    jobId={jobId}
                    proceedToPreview={() => {
                      setView(ViewType.SEND);
                    }}
                  />
                  <LegacyModal
                    className="preview-and-send-job-modal"
                    isOpen={view === ViewType.SEND}
                    size="lg"
                    onClose={() => setView(ViewType.EDIT)}
                  >
                    <PreviewAndSendJobControl
                      jobId={jobId}
                      sendJobToCustomer={sendJobToCustomer}
                    />
                  </LegacyModal>
                </>
              )}

              {job.bouncedAt && (
                <PreviewAndSendJobControl
                  jobId={jobId}
                  sendJobToCustomer={sendJobToCustomer}
                />
              )}
            </>
          )}
        </div>
      </AuthenticatedPageWrapper>

      <EditProposalLineItemSidebar
        allowProposalLineItemSelectionType
        isOpen={!!isEditProposalLineItemDrawOpen}
        proposalLineItemId={
          isEditProposalLineItemDrawOpen?.params.proposalLineItemId &&
          isEditProposalLineItemDrawOpen.params.proposalLineItemId !== 'new'
            ? isEditProposalLineItemDrawOpen.params.proposalLineItemId
            : undefined
        }
        onSaveSuccess={handleProposalLineItemPersist}
        onRemoveSuccess={handleProposalLineItemPersist}
        onClose={() => history.push(match.url)}
      />

      <Modal
        isOpen={!!errorModal}
        title={errorModal?.title ?? ''}
        onClose={() => setErrorModal(null)}
        size="sm"
      >
        <Modal.Body>{errorModal?.text}</Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            {errorModal?.secondaryText && errorModal?.secondaryAction && (
              <Button variant="outline" onClick={errorModal.secondaryAction}>
                {errorModal.secondaryText}
              </Button>
            )}
            {errorModal && (
              <Button onClick={errorModal.primaryAction}>
                {errorModal.primaryText}
              </Button>
            )}
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default EditJobPage;
